/**
 * PantryItemActionsSheet — bottom-sheet modal for quick actions on
 * one pantry row: Consumed / Wasted / Donated / Moved / Restocked /
 * Photo / Rename (alias) / Notes.
 *
 * All actions post to /api/pantry/{item_id}/events which:
 *   - Writes a fp_pantry_events row (audit trail)
 *   - Updates the pantry row's quantity + status + storage_location
 *   - Optionally appends the uploaded photo URL to the row's photos[]
 *
 * Called from the Pantry tab of MyFoodLists.
 */
import { useEffect, useRef, useState } from "react";
import {
  X, Check, MinusCircle, Trash2, HandHeart, Move, RefreshCw, Camera,
  AlertTriangle, HelpCircle, PackageOpen, Loader2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const RED   = "#7B2A2E";

const ACTIONS = [
  { key: "consumed",    label: "Consumed",          icon: Check,       color: "#059669", event: "consumed",           needsQty: true  },
  { key: "opened",      label: "Opened",            icon: PackageOpen, color: "#3B82F6", event: "opened" },
  { key: "restocked",   label: "Restocked",         icon: RefreshCw,   color: "#059669", event: "restocked",          needsQty: true  },
  { key: "moved",       label: "Moved to…",         icon: Move,        color: "#0F3D2E", event: "moved",              needsLoc: true  },
  { key: "wasted",      label: "Wasted / Spoiled",  icon: Trash2,      color: "#DC2626", event: "wasted" },
  { key: "expired",     label: "Expired",           icon: AlertTriangle,color:"#DC2626", event: "expired" },
  { key: "donated",     label: "Donated",           icon: HandHeart,   color: "#7B2A2E", event: "donated",            needsDonateTo: true },
  { key: "missing",     label: "Missing",           icon: HelpCircle,  color: "#6B7280", event: "missing" },
];

export default function PantryItemActionsSheet({ item, locations, open, onClose, onChange }) {
  const [action, setAction] = useState(null);
  const [qty, setQty]     = useState("");
  const [locId, setLocId] = useState("");
  const [donatedTo, setDonatedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setAction(null); setQty(""); setLocId(""); setDonatedTo(""); setNotes(""); setPhotoUrl(null); }
  }, [open]);

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    try {
      const body = {
        event_type:       action.event,
        notes:            notes.trim() || undefined,
        photo_url:        photoUrl || undefined,
        donated_to:       donatedTo.trim() || undefined,
      };
      if (action.key === "consumed" && qty !== "") {
        body.quantity_delta = -Math.abs(Number(qty));
      } else if (action.key === "restocked" && qty !== "") {
        body.quantity_delta = Math.abs(Number(qty));
      } else if (action.key === "moved") {
        body.to_location_id = locId || undefined;
      } else if (action.key === "consumed" && qty === "") {
        // "Consumed" with no qty = "consumed everything"
        body.new_quantity = 0;
      }
      const r = await http.post(`/pantry/${item.id}/events`, body);
      toast.success(`${action.label} · logged`);
      onChange?.(r.data?.pantry_item);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log the event");
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const up = await http.post("/lists/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(up.data?.url || null);
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  if (!open || !item) return null;
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
           style={{ color: GREEN }}
           data-testid="pantry-actions-sheet">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EED9AC" }}>
          <div className="flex items-center gap-2 min-w-0">
            {item.food_item_image && <img src={item.food_item_image} alt="" className="w-9 h-9 rounded-lg object-cover" />}
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{item.food_item_name || item.food_item_slug}</div>
              <div className="text-[11px] opacity-70">{item.quantity} {item.unit || "each"}{item.expiry_date ? ` · exp ${item.expiry_date}` : ""}</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-black/5" data-testid="pantry-actions-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!action ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ACTIONS.map((a) => {
                const Ico = a.icon;
                return (
                  <button key={a.key} onClick={() => setAction(a)}
                          className="rounded-2xl border-2 p-3 text-center hover:bg-amber-50 transition"
                          style={{ borderColor: "#E9D8B4" }}
                          data-testid={`pantry-action-${a.key}`}>
                    <Ico className="w-5 h-5 mx-auto mb-1" style={{ color: a.color }} />
                    <div className="text-xs font-semibold" style={{ color: GREEN }}>{a.label}</div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div>
              <button onClick={() => setAction(null)} className="mb-3 text-xs opacity-70 flex items-center gap-1">
                <ArrowRight className="w-3 h-3 rotate-180" /> Back
              </button>
              <div className="font-semibold text-lg mb-2 flex items-center gap-2">
                <action.icon className="w-5 h-5" style={{ color: action.color }} /> {action.label}
              </div>

              {action.needsQty && (
                <div className="mb-3">
                  <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
                    {action.key === "consumed" ? "How much did you use?" : "How much did you add?"}
                  </label>
                  <input type="number" step="0.5" min="0" value={qty}
                         onChange={(e) => setQty(e.target.value)}
                         placeholder={`e.g. 1 (${item.unit || "each"})`}
                         className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                         style={{ borderColor: "#E9D8B4" }}
                         data-testid="pantry-action-qty" />
                  {action.key === "consumed" && (
                    <p className="text-[10px] opacity-60 mt-1">Leave blank to mark as fully consumed.</p>
                  )}
                </div>
              )}

              {action.needsLoc && (
                <div className="mb-3">
                  <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Move to location</label>
                  <select value={locId} onChange={(e) => setLocId(e.target.value)}
                          className="w-full rounded-xl border px-3 py-2 text-sm outline-none bg-white"
                          style={{ borderColor: "#E9D8B4" }}
                          data-testid="pantry-action-loc">
                    <option value="">— Pick a location —</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.icon || "📦"} {l.is_private ? (l.alias || l.name) : l.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {action.needsDonateTo && (
                <div className="mb-3">
                  <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Donated to</label>
                  <input value={donatedTo} onChange={(e) => setDonatedTo(e.target.value)}
                         placeholder="Food bank / neighbour / community fridge"
                         className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                         style={{ borderColor: "#E9D8B4" }}
                         data-testid="pantry-action-donated-to" />
                </div>
              )}

              {/* Optional photo */}
              <div className="mb-3">
                <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
                  Photo (optional)
                </label>
                {photoUrl ? (
                  <div className="flex items-center gap-2">
                    <img src={photoUrl.startsWith("http") ? photoUrl : `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                         alt="" className="w-16 h-16 object-cover rounded-lg" />
                    <button onClick={() => setPhotoUrl(null)} className="text-xs underline opacity-70">
                      remove
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()}
                          className="rounded-full border-2 border-dashed px-3 py-2 text-xs font-semibold flex items-center gap-1"
                          style={{ borderColor: AMBER, color: GREEN }}
                          data-testid="pantry-action-photo">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                    Attach photo
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={(e) => uploadPhoto(e.target.files?.[0])}
                       data-testid="pantry-action-photo-input" />
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                          placeholder="Optional note for your future self."
                          className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                          style={{ borderColor: "#E9D8B4" }}
                          data-testid="pantry-action-notes" />
              </div>

              <button onClick={submit} disabled={busy}
                      className="w-full rounded-full py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ background: action.color }}
                      data-testid="pantry-action-submit">
                {busy
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging…</>
                  : <><Check className="w-4 h-4" /> Log {action.label}</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
